// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 09-data-brokering.md
// @greenlight-spec: 19-design-system.md
import * as React from 'react';

import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { downloadTextFile } from '@/lib/download';
import { generateM2mCertificate } from '@/lib/generateM2mCertificate';

export interface PrivateKeyEntryProps {
  id: string;
  label: string;
  value: string;
  onChange: (privateKeyPem: string, generated: boolean) => void;
  /** When true the private key is held in form state only — never rendered. */
  generated: boolean;
  /** `private_key_jwt` only. GitHub App keys are issued by GitHub. */
  allowGenerate: boolean;
  placeholder: string;
}

export function PrivateKeyEntry({
  id,
  label,
  value,
  onChange,
  generated,
  allowGenerate,
  placeholder,
}: PrivateKeyEntryProps): React.JSX.Element {
  const [certificatePem, setCertificatePem] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  async function handleGenerate(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const result = await generateM2mCertificate();
      setCertificatePem(result.certificatePem);
      onChange(result.privateKeyPem, true);
    } catch {
      setError('Could not generate a certificate. Try again or paste an existing private key.');
    } finally {
      setBusy(false);
    }
  }

  function handleStartOver(): void {
    setCertificatePem(null);
    setError(null);
    onChange('', false);
  }

  if (allowGenerate && generated) {
    return (
      <div className="space-y-2" data-testid="private-key-entry">
        <p className="text-xs font-medium text-foreground">{label}</p>
        <p
          className="rounded-gl border border-border bg-muted/30 px-3 py-2 text-[11.5px] leading-snug text-muted-foreground"
          data-testid="generated-certificate-note"
          role="status"
        >
          Certificate generated. Download the public certificate, upload it in the provider’s M2M
          mapping, and copy the Certificate ID into the field above. The private key stays in this
          form until you register — it is never shown.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-11"
            disabled={!certificatePem}
            onClick={() => {
              if (certificatePem) {
                downloadTextFile(
                  'greenlight-m2m-certificate.pem',
                  certificatePem,
                  'application/x-pem-file',
                );
              }
            }}
          >
            Download public certificate
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-11"
            onClick={handleStartOver}
          >
            Start over
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="private-key-entry">
      {allowGenerate && (
        <div className="space-y-2">
          <Button
            type="button"
            size="sm"
            className="h-11"
            disabled={busy}
            onClick={() => void handleGenerate()}
          >
            {busy ? 'Generating…' : 'Generate certificate'}
          </Button>
          {error ? <p className="text-[11.5px] text-destructive">{error}</p> : null}
          <p className="text-[11.5px] leading-snug text-muted-foreground">
            Or paste an existing private key.
          </p>
        </div>
      )}
      <label htmlFor={id} className="text-xs font-medium text-foreground">
        {label}
      </label>
      <Textarea
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value, false)}
        placeholder={placeholder}
        autoComplete="off"
        className="min-h-28 font-mono"
      />
    </div>
  );
}
